import { get, isEmpty, last, map } from 'lodash';

import { NEGATIVE_ZERO } from '../../common/constants/chart-options';
import { createSelector } from 'reselect';
import { parseTime } from '../../utils/chart-utils';

// Domain Coverage
export const rawDomainCoverageSelector = state => get( state, 'dmarc.domainCoverage', {} );
export const domainCoverageIsLoadingSelector = state => get( state, 'dmarc.domainCoverageIsLoading', false );
export const domainCoverageIsLoadedSelector = state => !get( state, 'dmarc.domainCoverageIsLoading', false ) && !isEmpty( get( state, 'dmarc.domainCoverage', {} ) );
export const prevDomainCoverageParamsSelector = state => get( state, 'dmarc.domainCoverageParams' );

export const coverageTableIsExportingGridToCSV = state => get( state, 'exportReport.isExportingCSV.coverage', false );

export const flattenedDomainCoverageSelector = createSelector( rawDomainCoverageSelector, ( rawData ) => {
    return map( rawData, ( row, date ) => {
        const { invalid, monitor, none, quarantine, reject, total } = row;

        const totalInvalid = invalid;
        const totalMonitor = monitor;
        const totalNone = none;
        const totalQuarantine = quarantine;
        const totalReject = reject;

        let percentInvalid = 0,
            percentMonitor = 0,
            percentNone = 0,
            percentQuarantine = 0,
            percentReject = 0;

        if( total > 0 ){
            percentInvalid = ( totalInvalid / total ) * 100;
            percentMonitor = ( totalMonitor / total ) * 100;
            percentNone = ( totalNone / total ) * 100;
            percentQuarantine = ( totalQuarantine / total ) * 100;
            percentReject = ( totalReject / total ) * 100;
        };

        return {
            date,
            percentInvalid: percentInvalid.toFixed( 2 ),
            percentMonitor: percentMonitor.toFixed( 2 ),
            percentNone: percentNone.toFixed( 2 ),
            percentQuarantine: percentQuarantine.toFixed( 2 ),
            percentReject: percentReject.toFixed( 2 ),
            total,
            totalInvalid,
            totalMonitor,
            totalNone,
            totalQuarantine,
            totalReject,
        };
    } );
} );

export const domainCoverageVisSelector = createSelector( rawDomainCoverageSelector, ( rawData ) => {
    let visData = map( rawData, ( row, date ) => {
        const { invalid, monitor, none, quarantine, reject } = row;

        let percentInvalid = 0,
            percentMonitor = 0,
            percentQuarantine = 0,
            percentReject = 0;

        if( row.total > 0 ){
            percentInvalid = ( ( invalid + none ) / row.total ) * 100;
            percentMonitor = ( monitor / row.total ) * 100;
            percentQuarantine = ( quarantine / row.total ) * 100;
            percentReject = ( reject / row.total ) * 100;
        };

        return {
            date: parseTime( '%Y-%m-%d' )( date ),
            invalid: ( percentInvalid === 0 ) ? NEGATIVE_ZERO : percentInvalid * -1,
            monitor: ( percentMonitor === 0 ) ? NEGATIVE_ZERO : percentMonitor * -1,
            negativeSpace: -100 + ( percentMonitor + percentInvalid ),
            positiveSpace: 100 - ( percentQuarantine + percentReject ),
            quarantine: percentQuarantine,
            reject: percentReject
        };
    } );

    return visData;
} );

export const domainsAtRejectSelector = createSelector(
    flattenedDomainCoverageSelector,
    ( domainCoverageData ) => {
        const currentTotalReject = get( last( domainCoverageData ), 'totalReject', 0 );
        const currentPercentReject = get( last( domainCoverageData ), 'percentReject', '0.00' );
        const rejectDelta = currentTotalReject - get( domainCoverageData, '[0].totalReject', 0 );

        let rejectDeltaRate = ( ( rejectDelta / currentTotalReject ) * 100 ) || 0;

        return {
            currentPercentReject,
            currentTotalReject,
            rejectDelta,
            rejectDeltaRate: rejectDeltaRate.toFixed( 2 )
        };
    }
);

// Progress Statements
export const rejectCountSelector = createSelector( flattenedDomainCoverageSelector, ( domainCoverageData ) =>
    get( last( domainCoverageData ), 'totalReject', 0 )
);
export const quarantineCountSelector = createSelector( flattenedDomainCoverageSelector, ( domainCoverageData ) =>
    get( last( domainCoverageData ), 'totalQuarantine', 0 )
);
export const monitorCountSelector = createSelector( flattenedDomainCoverageSelector, ( domainCoverageData ) =>
    get( last( domainCoverageData ), 'totalMonitor', 0 )
);
export const noRecordCountSelector = createSelector( flattenedDomainCoverageSelector, ( domainCoverageData ) =>
    get( last( domainCoverageData ), 'totalNone', 0 )
);
export const invalidCountSelector = createSelector( flattenedDomainCoverageSelector, ( domainCoverageData ) =>
    get( last( domainCoverageData ), 'totalInvalid', 0 )
);
